import React, { useEffect } from 'react';
import { X, Pen } from 'lucide-react';

// Import hooks
import { useNotifications } from './hooks/useNotifications';
import { useReportForm } from './hooks/useReportForm';
import { useCustomerSearch } from './hooks/useCustomerSearch';
import { useServiceSearch } from './hooks/useServiceSearch';
import { useReportActions } from './hooks/useReportActions';

// Import components
import BasicInfoSection from './components/BasicInfoSection';
import CustomerSection from './components/CustomerSection';
import TechnicianSection from './components/TechnicianSection';
import JobLocationSection from './components/JobLocationSection';
import TreatmentSection from './components/TreatmentSection';

const TreatmentReportEditor = ({
  report = null,
  onSave,
  onSaveAndSign,
  onCancel,
  userProfile,
  onOpenAddCustomerModal,
  initialContext
}) => {
  // Initialize hooks
  const notifications = useNotifications();
  const reportForm = useReportForm(report, userProfile, initialContext);
  const customerSearch = useCustomerSearch(reportForm.getFranchiseeId, reportForm.updateFormField);
  const serviceSearch = useServiceSearch(reportForm.formData, reportForm.setFormData);
  const reportActions = useReportActions(report, reportForm.getFranchiseeId, notifications.showNotification);

  // Initialize customer from report data or initial context
  useEffect(() => {
    if (report) {
      customerSearch.initializeSelectedCustomer(report);
    } else if (initialContext?.customer) {
      customerSearch.handleCustomerSelect(initialContext.customer);
    }
  }, [report]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    await reportActions.handleSubmit(reportForm.formData, onSave);
  };

  // Handle save & sign — save then open signing modal
  const handleSaveAndSign = async () => {
    if (onSaveAndSign) {
      await reportActions.handleSubmit(reportForm.formData, onSaveAndSign);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {report ? 'Edit Treatment Report' : 'Add Treatment Report'}
            </h2>
            {report && report.report_number ? (
              <p className="text-sm text-white/80 mt-0.5">{report.report_number}</p>
            ) : !report && (
              <p className="text-sm text-white/80 mt-0.5">Create a new treatment report</p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column - Basic Info & Customer */}
            <div className="space-y-6">
              <BasicInfoSection
                formData={reportForm.formData}
                updateFormField={reportForm.updateFormField}
                isEditing={!!report}
                jobLinked={!!reportForm.formData.job_id}
              />

              <CustomerSection
                selectedCustomer={customerSearch.selectedCustomer}
                customerSearch={customerSearch.customerSearch}
                setCustomerSearch={customerSearch.setCustomerSearch}
                customerResults={customerSearch.customerResults}
                showCustomerDropdown={customerSearch.showCustomerDropdown}
                searchCustomers={customerSearch.searchCustomers}
                handleCustomerSelect={customerSearch.handleCustomerSelect}
                clearCustomerSelection={customerSearch.clearCustomerSelection}
                onAddNewCustomer={() => onOpenAddCustomerModal && onOpenAddCustomerModal(customerSearch.handleCustomerSelect)}
                formData={reportForm.formData}
                updateFormField={reportForm.updateFormField}
              />

              <JobLocationSection
                formData={reportForm.formData}
                updateFormField={reportForm.updateFormField}
                selectedCustomer={customerSearch.selectedCustomer}
              />
            </div>

            {/* Right Column - Treatment & Services */}
            <div className="space-y-6">
              <TreatmentSection
                formData={reportForm.formData}
                updateFormField={reportForm.updateFormField}
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              {onSaveAndSign && (
                <button
                  type="button"
                  onClick={handleSaveAndSign}
                  disabled={reportActions.isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Pen size={16} />
                  {reportActions.isLoading ? 'Saving...' : 'Save & Sign Now'}
                </button>
              )}
              <button
                type="submit"
                disabled={reportActions.isLoading}
                className="px-8 py-3 bg-gradient-to-r from-pest-green to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reportActions.isLoading ? 'Saving...' : 'Save Report'}
              </button>
            </div>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TreatmentReportEditor;